import { useEffect, useState } from "react";
import Image from "next/image";
import LazyImage from "@/my_components/Common/LazyImage";
import Link from "next/link";
import { useTranslation } from "react-i18next";

// load in other components
import Pagination from "@/my_components/Common/Pagination";

// load in data
import BlogData from "@/data/blog/blog.json";

// load in utilities
import { ROUTE } from "@/route/app_routes.js";
import { image_url } from "@/helper/Utilities";
import { blogPostSlug, filterBlogPosts } from "@/helper/SpecialHelpers";

const PAGE_SIZE = 9;

const BlogList = ({ isPagination, search, activeTag }) => {

  const { t } = useTranslation("blog");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [search, activeTag]);

  const filtered = filterBlogPosts(BlogData.blogList, search, activeTag);

  const showFeatured = page === 1 && !search.trim() && activeTag === "All";
  const featuredPost = showFeatured ? filtered[0] : null;
  const gridSource = showFeatured ? filtered.slice(1) : filtered;

  const totalPages = Math.ceil(gridSource.length / PAGE_SIZE) || 1;
  const startIndex = (page - 1) * PAGE_SIZE;
  const pagePosts = gridSource.slice(startIndex, startIndex + PAGE_SIZE);

  const handleClick = (num) => {
    setPage(num);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (filtered.length === 0) {
    return (
      <div className="row">
        <div className="col-lg-10 offset-lg-1">
          <div className="rbt-blog-empty-state text-center mt--30">
            <i className="feather-search"></i>
            <h5>{t("list.emptyTitle", "No articles match your search")}</h5>
            <p>{t("list.emptyDescription", "Try a different keyword or clear the topic filter.")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="row">
        <div className="col-lg-10 offset-lg-1 mt_dec--30">

          {featuredPost && (
            <div className="col-12 mt--30">
              <div className="rbt-card variation-02 height-auto rbt-hover rbt-blog-featured-card">
                <div className="rbt-card-img">
                  <Link href={ROUTE.blog_details + blogPostSlug(featuredPost)}>
                    <Image
                      src={image_url(featuredPost.img)}
                      width={1085}
                      height={520}
                      priority
                      alt={t(`posts.${featuredPost.id}.title`, featuredPost.title)}
                    />
                  </Link>
                </div>

                <div className="rbt-card-body">
                  <ul className="rbt-meta">
                    <li><i className="feather-clock"></i> {featuredPost.date}</li>
                    <li><i className="feather-user"></i> {featuredPost.name}</li>
                  </ul>
                  <h3 className="rbt-card-title">
                    <Link href={ROUTE.blog_details + blogPostSlug(featuredPost)}>
                      {t(`posts.${featuredPost.id}.title`, featuredPost.title)}
                    </Link>
                  </h3>
                  <p className="rbt-card-text"> {t(`posts.${featuredPost.id}.desc`, featuredPost.desc)} </p>
                  <div className="rbt-card-bottom">
                    <Link className="transparent-button" href={ROUTE.blog_details + blogPostSlug(featuredPost)}>
                      {t("list.readArticle", "Read Article")}
                      <i>
                        <svg width="17" height="12" xmlns="http://www.w3.org/2000/svg">
                          <g stroke="#27374D" fill="none" fillRule="evenodd">
                            <path d="M10.614 0l5.629 5.629-5.63 5.629" />
                            <path strokeLinecap="square" d="M.663 5.572h14.594" />
                          </g>
                        </svg>
                      </i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="col-12 mt--30">
            <div className="row g-5">
              {pagePosts.map((post) => (
                <div className="col-lg-4 col-md-6 col-12" key={post.id}>
                  <div className="rbt-card variation-02 rbt-hover h-100">
                    <div className="rbt-card-img">
                      <Link href={ROUTE.blog_details + blogPostSlug(post)}>
                        <LazyImage
                          src={image_url(post.img)}
                          width={400}
                          height={230}
                          alt={t(`posts.${post.id}.title`, post.title)}
                        />
                      </Link>
                    </div>
                    <div className="rbt-card-body">
                      <ul className="rbt-meta">
                        <li><i className="feather-clock"></i> {post.date}</li>
                      </ul>
                      <h5 className="rbt-card-title">
                        <Link href={ROUTE.blog_details + blogPostSlug(post)}>
                          {t(`posts.${post.id}.title`, post.title)}
                        </Link>
                      </h5>
                      <p className="rbt-card-text">{t(`posts.${post.id}.desc`, post.desc)}</p>
                      <div className="rbt-card-bottom">
                        <Link className="transparent-button" href={ROUTE.blog_details + blogPostSlug(post)}>
                          {t("list.readArticle", "Read Article")}
                          <i>
                            <svg width="17" height="12" xmlns="http://www.w3.org/2000/svg">
                              <g stroke="#27374D" fill="none" fillRule="evenodd">
                                <path d="M10.614 0l5.629 5.629-5.63 5.629" />
                                <path strokeLinecap="square" d="M.663 5.572h14.594" />
                              </g>
                            </svg>
                          </i>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {isPagination && totalPages > 1 ? (
        <div className="row">
          <div className="col-lg-12 mt--60">
            <Pagination
              totalPages={totalPages}
              pageNumber={page}
              handleClick={handleClick}
            />
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};

export default BlogList;
